import { EmployeeController, WalletController } from '../../controllers';
import { appStore, employeeStore } from '../../stores';
import { MimaToastUtil } from '../../components';
import { object, string, number } from 'yup';
import constant from '../../utils/constant';
import { EditEmployee } from '../../pages';
import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { phoneRegExp } from '../../utils/utils';

const EditEmployeeContainer = ({
	closeModal,
	success,
	stage2,
	stage3,
	showStage2,
	showStage3,
	showEditSuccess,
	showStage1,
}) => {
	const [loading, setLoading] = useState(false);
	const [accountName, setAccountName] = useState('');

	const userInfoSchema = () =>
		object({
			fullname: string()
				.required('Full Name is required')
				.test('fullName', 'First and Last name is required', function (value) {
					const names = value ? value.split(' ') : [];
					return names.length > 1 && names[0].length > 1 && names[1].length > 1;
				}),
			mobile: string()
				.matches(phoneRegExp, 'Phone number is not valid')
				.required('Phone number is required'),
			email: string()
				.trim()
				.email('Enter a valid Email')
				.required('Email Address is required'),
			gender: string().required('Gender is required'),
		});
	const jobInfoSchema = () =>
		object({
			jobTitle: string().required('Job role is required'),
			employmentType: string().required('Employment Type is required'),
			settlementCycle: string().required('Settlement Cycle is required'),
			grossPay: number().test(function () {
				const { grossPay, employmentType } = this.parent;
				return grossPay === 0 && employmentType === 'FULLTIME'
					? this.createError({
						message: 'Monthly gross pay is required for a full time position',
						path: 'grossPay',
					})
					: true;
			}),
			basicSalary: number().test(function () {
				const { basicSalary, employmentType, deductions } = this.parent;
				return basicSalary === 0
				&& employmentType === 'FULLTIME'
				&& (deductions.pension || deductions.tax)
					? this.createError({
						message: 'Monthly basic salary is required to calculate Pension and NHF',
						path: 'basicSalary',
					})
					: true;
			}),
		});
	const bankInfoSchema = () =>
		object({
			bankCode: string().required('Select a bank'),
			accountNumber: string().required('Account Number is required'),
		});

	const verifyAccountNumber = async payload => {
		if (
			payload.beneficiaryBank &&
			payload.accountNumber &&
			payload.accountNumber.length === 10
		) {
			const {
				status,
				data,
				errorMessage,
			} = await WalletController.verifyAccountNumber(payload);
			if (status === constant.Success) {
				return data.accountName;
			} else {
				MimaToastUtil.error({
					message: errorMessage,
				});
			}
		}
		return '';
	};

	const onSubmitUserInfo = data => {
		const names = data.fullname.split(' ');
		delete data.fullname;
		const payload = {
			userInfo: {
				firstname: names[0],
				lastname: names[1],
				...data,
			},
		};
		employeeStore.setTrackCreateEmployee(payload);

		localStorage.setItem(constant.RouteParams, JSON.stringify(payload));
		showStage2();
	};

	const onSubmitJobInfo = data => {
		let payload = JSON.parse(localStorage.getItem(constant.RouteParams));
		if (Object.keys(payload).length === 0) {
			payload = {};
			const {
				lastname,
				firstname,
				mobile,
				email,
				gender,
			} = employeeStore.selectedEmployee.user;
			payload.userInfo = { lastname, firstname, mobile, email, gender };
		}
		payload.jobInfo = data;
		delete payload.jobInfo.departmentName;
		delete payload.jobInfo.employeeGroupName;
		employeeStore.setTrackCreateEmployee(payload);
		localStorage.setItem(constant.RouteParams, JSON.stringify(payload));
		showStage3();
	};

	const onSubmitBankInfo = async data => {
		setLoading(true);
		data.accountName = accountName ? accountName : data.accountName;
		let payload = JSON.parse(localStorage.getItem(constant.RouteParams));
		if (!payload || Object.keys(payload).length === 0) {
			payload = {};
			const {
				lastname,
				firstname,
				mobile,
				email,
				gender,
			} = employeeStore.selectedEmployee.user;
			payload.userInfo = { lastname, firstname, mobile, email, gender };
			const {
				employmentType,
				jobTitle,
				startDate,
				grossPay,
				deductions,
			} = employeeStore.selectedEmployee;
			delete deductions.hmo;
			payload.jobInfo = {
				employmentType,
				jobTitle,
				startDate,
				grossPay,
				deductions,
			};
		}
		payload.bankDetails = data;
		employeeStore.setTrackCreateEmployee(payload);
		const { status, errorMessage } = await EmployeeController.updateEmployee(
			payload,
			employeeStore.selectedEmployee?.user?._id
		);
		if (status === constant.Success) {
			setLoading(false);
			employeeStore.setTrackCreateEmployee({});
			employeeStore.setSelectedEmployee({});
			MimaToastUtil.success({
				message: status,
			});
			closeModal();
			return showEditSuccess();
		}
		setLoading(false);
		employeeStore.setTrackCreateEmployee({});
		localStorage.setItem(constant.RouteParams, JSON.stringify({}));
		MimaToastUtil.error({
			message: errorMessage,
		});
		return closeModal();
	};



	return (
		<EditEmployee
			stage2={stage2}
			stage3={stage3}
			trackCreate={employeeStore.trackCreate}
			accountName={accountName}
			employee={employeeStore.selectedEmployee}
			employeeGroups={employeeStore.groups}
			setAccountName={setAccountName}
			userInfoSchema={userInfoSchema}
			jobInfoSchema={jobInfoSchema}
			bankInfoSchema={bankInfoSchema}
			onSubmitUserInfo={onSubmitUserInfo}
			onSubmitJobInfo={onSubmitJobInfo}
			onSubmitBankInfo={onSubmitBankInfo}
			verifyAccountNumber={verifyAccountNumber}
			showStage2={showStage2}
			setLoading={setLoading}
			loading={loading}
			banks={appStore.banks.map(bank => {
				return { id: bank.key, value: bank.value };
			})}
			showStage3={showStage3}
			success={success}
			showEditSuccess={showEditSuccess}
			closeModal={closeModal}
			showStage1={showStage1}
		/>
	);
};

export default observer(EditEmployeeContainer);
